import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { useFeatures } from "@itaas/core/subscription/hooks";
import { HOSPITOL_UI_PERMISSIONS } from "@/core/permissions";
import {
  getEditionHiddenMenuPaths,
  getTenantEdition,
} from "@/core/navigation/edition-menu-mask";
import { redirectToSuiteLogin } from "@/core/config";
import { routes } from "@/router/all_routes";

interface ProtectedRouteProps {
  children: ReactNode;
  permissions?: string[];
  feature?: string;
}

function isPathEditionDenied(pathname: string): boolean {
  const hidden = getEditionHiddenMenuPaths(getTenantEdition());
  for (const p of hidden) {
    if (pathname === p || pathname.startsWith(`${p}/`)) return true;
  }
  return false;
}

export function ProtectedRoute({
  children,
  permissions = [...HOSPITOL_UI_PERMISSIONS.access],
  feature = "hospitol",
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasAnyPermission } = useAuth();
  const { hasFeature, isLoading: featuresLoading } = useFeatures();
  const location = useLocation();

  if (isLoading || featuresLoading) {
    return <div className="text-center py-5">Chargement…</div>;
  }

  if (!isAuthenticated) {
    redirectToSuiteLogin();
    return (
      <div className="ph-login-page">
        <div className="ph-login-card text-muted">Redirection vers ITaaS Suite…</div>
      </div>
    );
  }

  if (!hasFeature(feature as "hospitol")) {
    return (
      <div className="alert alert-warning m-4">
        Le module Hospitol n&apos;est pas activé sur votre abonnement.
      </div>
    );
  }

  if (!hasAnyPermission(permissions)) {
    return (
      <div className="alert alert-danger m-4">
        Vous n&apos;avez pas la permission requise pour cette section.
      </div>
    );
  }

  if (isPathEditionDenied(location.pathname)) {
    return (
      <div className="alert alert-danger m-4">
        Cette section n&apos;est pas disponible pour le profil / édition Pharmacy.
      </div>
    );
  }

  return <>{children}</>;
}

export function RootRedirect() {
  return <Navigate to={routes.tenantDashboard} replace />;
}
